import { last, pipe, prop } from "ramda";
import { useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "../../components/Icon";
import { Layout } from "../../components/Layout";
import { Msg, MsgProvider } from "../../components/Msg";
import { useMsg } from "../../components/Msg/Msg";
import { routes } from "../../routes";
import { I18nContext } from "../I18n/I18nProvider";
import { Loaders, QueryRenderer } from "../QM/QueryRenderer";
import { WithIsFirstSession } from "./StartSession.page";
import { StepperRightMenu } from "./StepperRightMenu";
import {
  useUserReflectionMutation,
  useUserSessionMutation,
  useUserSessionQuery,
} from "./api";
import { sessionsMessages } from "./messages";
import { Finished } from "./steps/Finished";
import { useSessionStepsDefinition, useSteps } from "./steps/useSessionSteps";
import { EvaluationModal } from "../Sessions/EvaluationModal";
const INITIAL_ADJUST = false;
const INITIAL_INDEX = 0;
// const INITIAL_ADJUST = true;
// const INITIAL_INDEX = 3;

function SessionPageInner({ isFirstSession }) {
  const [adjust, setAdjust] = useState(INITIAL_ADJUST);
  const [isFinished, setIsFinished] = useState(false);
  const [data, setData] = useState({});
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);

  const { i18n } = useContext(I18nContext);
  const msg = useMsg();
  const navigate = useNavigate();
  const query = useUserSessionQuery();
  const sessionMutation = useUserSessionMutation({
    withPrevActionSteps: !isFirstSession,
  });
  const reflectionMutation = useUserReflectionMutation({ adjust });
  const submitDisabled =
    sessionMutation.isPending || reflectionMutation.isPending;

  const {
    ALIGN,
    REFLECT,
    MOTIVATION,
    AREA,
    GOAL,
    FIRST_ACTIONS,
    REVIEW_ACTIONS,
  } = useSessionStepsDefinition();
  const steps = useMemo(
    () =>
      isFirstSession
        ? [AREA, GOAL, MOTIVATION, FIRST_ACTIONS]
        : adjust
          ? [ALIGN, REFLECT, AREA, GOAL, MOTIVATION, REVIEW_ACTIONS]
          : [ALIGN, REFLECT, REVIEW_ACTIONS],
    [
      ALIGN,
      AREA,
      FIRST_ACTIONS, // ACTION_STEPS -> sessionSubmit (first session)
      GOAL,
      MOTIVATION,
      REFLECT, // PREV_ACTION_STEPS -> reflectionSubmit (adjust)
      REVIEW_ACTIONS, // ACTION_STEPS + PREV_ACTION_STEPS -> sessionSubmit (adjust) or reflectionSubmit (not adjusted)
      adjust,
      isFirstSession,
    ],
  );
  const {
    activeStep: { StepComponent, ...activeStep } = {},
    activeStepIndex,
    nextStep,
    prevStep,
    isLast,
  } = useSteps({ steps, initialIndex: INITIAL_INDEX });

  const proceedToNextStep = useCallback(
    (componentData = {}) => {
      setData((prev) => ({
        ...prev,
        ...componentData,
      }));
      nextStep();
    },
    [nextStep],
  );

  const submitByStep = useMemo(() => {
    const onSuccess = () => {
      if (isLast) {
        setIsFinished(true);
        setShowEvaluationModal(true);
      } else nextStep();
    };
    const sessionSubmit = (variables) => {
      // debugger;
      return sessionMutation.mutate(variables, { onSuccess });
    };
    const reflectionSubmit = (variables) => {
      // debugger;
      return reflectionMutation.mutate(variables, { onSuccess });
    };

    if (isFirstSession) {
      return {
        [FIRST_ACTIONS.stepName]: sessionSubmit,
      };
    } else if (adjust) {
      return {
        [REFLECT.stepName]: reflectionSubmit,
        [REVIEW_ACTIONS.stepName]: sessionSubmit,
      };
    } else {
      return {
        [REVIEW_ACTIONS.stepName]: reflectionSubmit,
      };
    }
  }, [
    adjust,
    FIRST_ACTIONS.stepName,
    isFirstSession,
    isLast,
    nextStep,
    REFLECT.stepName,
    reflectionMutation,
    REVIEW_ACTIONS.stepName,
    sessionMutation,
  ]);

  const handleNext = useCallback(
    (componentData) => {
      const submitMaybe = submitByStep[activeStep.stepName];
      console.log("[SessionPage.hndlNxt]", activeStep.stepName, {
        componentData,
        data,
        submitMaybe,
        adjust,
      });
      if (submitMaybe) submitMaybe({ ...data, ...componentData });
      else proceedToNextStep(componentData);
    },
    [activeStep.stepName, adjust, data, proceedToNextStep, submitByStep],
  );

  const handleBack = useCallback(
    (componentData) => {
      setData((prev) => ({ ...prev, ...componentData }));
      prevStep();
    },
    [prevStep, setData],
  );

  console.log("[SessionPage.rndr]", {
    isFetching: query.isFetching,
    data,
    activeStepIndex,
    steps,
    activeStep,
    adjust,
  });

  return (
    <Layout
      header={{
        actionButton: {
          variant: "text",
          color: "error",
          children: msg("sessions.new.aside.end-button"),
          onClick: () =>
            navigate(isFirstSession ? routes.dashboard : routes.sessions),
          startIcon: <Icon name="StopCircle" />,
        },
      }}
      rightMenuContent={
        <StepperRightMenu
          heading={
            <>
              <Msg id="sessions.new.aside.title" />
              &nbsp; {i18n.formatLocalMaybe(new Date(), "P")}
            </>
          }
          activeStepIndex={activeStepIndex}
          steps={steps}
        />
      }
    >
      {query.isFetching ? (
        <QueryRenderer isPending loaderEl={<Loaders.CircularBlock spaced />} />
      ) : isFinished ? (
        <Finished />
      ) : (
        <StepComponent
          step={activeStep}
          stepper={{
            currentIndex: activeStepIndex,
            totalCount: steps.length,
            isLast,
            handleNext,
            handleBack,
          }}
          data={data}
          submitDisabled={submitDisabled}
          // edit extras
          motivation={query.data?.motivation}
          lastReflection={query.data?.lastReflection}
          previousActionSteps={query.data?.actionSteps ?? []}
          previousArea={query.data?.areaOfDevelopment ?? ""}
          previousGoal={query.data?.longTermGoal ?? ""}
          adjust={adjust}
          setAdjust={setAdjust}
        />
      )}

      {showEvaluationModal && (
        <EvaluationModal
          open={true}
          onClose={() => setShowEvaluationModal(false)}
        />
      )}
    </Layout>
  );
}

export function SessionPage() {
  return (
    <MsgProvider messages={sessionsMessages}>
      <WithIsFirstSession
        loaderEl={
          <Layout>
            <Loaders.CircularBlock spaced />
          </Layout>
        }
      >
        {({ isFirstSession, lastSessionId }) => (
          <SessionPageInner isFirstSession={isFirstSession} />
        )}
      </WithIsFirstSession>
    </MsgProvider>
  );
}
